import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["className", "disabled", "id", "image", "imageSrc", "inline", "label", "alignRadioTop", "dangerouslyForceFocusStyling", "dangerouslyForceHoverStyling"];
import React from 'react';
import { and } from 'airbnb-prop-types';
import classNames from 'classnames';
import classes from '@nerdwallet/base-styles/classes';
import { useDeprecatedPropsWarning } from '@nerdwallet/deprecate';
import { useDeprecatedClassNameStylePropsWarning } from '../../internal/deprecated-classname-style-props';
import { ActionBlock } from '../ActionBlock/ActionBlock';
import styles from './Radio.module.less';
/**
 * Custom prop type validator that checks string length.
 */

var maxLineLength = {};

maxLineLength.isRequired = function (props, propName) {
  var componentName = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'ANONYMOUS';

  if (props[propName]) {
    var value = props[propName];
    var maxChars = 70;

    if (typeof value === 'string') {
      return value.length <= maxChars ? null : new Error("Keep ".concat(propName, " in ").concat(componentName, " under ").concat(maxChars, " characters. Line length on ").concat(propName, " is ").concat(value.length - maxChars, " characters too long."));
    }
  }

  return null;
};

var BlockLabel = function BlockLabel(_ref) {
  var className = _ref.className,
      children = _ref.children,
      disabled = _ref.disabled,
      dangerouslyForceFocusStyling = _ref.dangerouslyForceFocusStyling,
      dangerouslyForceHoverStyling = _ref.dangerouslyForceHoverStyling,
      alignRadioTop = _ref.alignRadioTop;
  return /*#__PURE__*/React.createElement(ActionBlock, {
    component: "span",
    mode: "group",
    disabled: disabled,
    className: classNames([className, styles.label, styles['label--block'],
    /**
     * When the label of a radio spans multiple lines, it can
     * be a desirable product use case to top-align the radio indicator.
     * This style is opted into with the alignRadioTop prop.
     */
    classes({
      'align-items-flex-start': alignRadioTop,
      'align-items-center': !alignRadioTop
    })]),
    dangerouslyForceHoverStyling: dangerouslyForceHoverStyling,
    dangerouslyForceFocusStyling: dangerouslyForceFocusStyling
  }, children);
}; // eslint-disable-next-line react/prop-types


var InlineLabel = function InlineLabel(_ref2) {
  var className = _ref2.className,
      children = _ref2.children;
  return /*#__PURE__*/React.createElement("span", {
    className: classNames([className, styles.label, styles['label--inline']])
  }, children);
};
/**
 * A single radio option with label. Can be rendered in a block or inline.
 */


export var RadioOption = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var className = props.className,
      disabled = props.disabled,
      id = props.id,
      image = props.image,
      imageSrc = props.imageSrc,
      inline = props.inline,
      label = props.label,
      alignRadioTop = props.alignRadioTop,
      dangerouslyForceFocusStyling = props.dangerouslyForceFocusStyling,
      dangerouslyForceHoverStyling = props.dangerouslyForceHoverStyling,
      otherProps = _objectWithoutProperties(props, _excluded);

  useDeprecatedPropsWarning({
    component: 'RadioOption',
    deprecatedProps: [{
      version: '10.4.0',
      deprecated: 'inline',
      migration: 'Inline radio options are no longer recommended for use in designs.'
    }],
    props: props
  });
  useDeprecatedClassNameStylePropsWarning('RadioOption', {
    className: className,
    style: otherProps.style
  });
  var LabelComponent = inline ? InlineLabel : BlockLabel;
  return /*#__PURE__*/React.createElement("label", {
    htmlFor: id,
    ref: ref
  }, /*#__PURE__*/React.createElement("input", _extends({
    type: "radio",
    disabled: disabled,
    className: classNames([styles.radio]),
    id: id
  }, otherProps)), /*#__PURE__*/React.createElement(LabelComponent, {
    className: className,
    disabled: disabled,
    alignRadioTop: alignRadioTop
    /** Dangerous props used for testing. */
    ,
    dangerouslyForceFocusStyling: dangerouslyForceFocusStyling,
    dangerouslyForceHoverStyling: dangerouslyForceHoverStyling
  }, /*#__PURE__*/React.createElement("span", {
    className: styles['radio-wrapper']
  }, /*#__PURE__*/React.createElement("span", {
    className: styles['radio-back']
  }), /*#__PURE__*/React.createElement("span", {
    className: styles['radio-front']
  })), label, (image || imageSrc) && /*#__PURE__*/React.createElement("div", {
    className: styles['radio-image-container']
  }, image || /*#__PURE__*/React.createElement("img", {
    src: imageSrc,
    alt: ""
  }))));
});
RadioOption.displayName = 'RadioOption';

if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line global-require
  var PropTypes = require('prop-types');

  RadioOption.propTypes = {
    /** @deprecated */
    className: PropTypes.string,
    disabled: PropTypes.bool,
    id: PropTypes.string,

    /** A component rendering an image. */
    image: PropTypes.node,

    /** A path to an image resource. */
    imageSrc: PropTypes.string,
    inline: PropTypes.bool,
    label: and([maxLineLength, PropTypes.node]).isRequired,

    /** Boolean flag to top-align the radio button when the radio text spans multiple lines. */
    alignRadioTop: PropTypes.bool,

    /** Force the component to use its focus styles. */
    dangerouslyForceFocusStyling: PropTypes.bool,

    /** Force the component to use its hover styles. */
    dangerouslyForceHoverStyling: PropTypes.bool
  };
}

RadioOption.defaultProps = {
  className: null,
  disabled: false,
  id: null,
  image: null,
  imageSrc: null,
  inline: false,
  alignRadioTop: false,
  dangerouslyForceFocusStyling: false,
  dangerouslyForceHoverStyling: false
};